import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/zip';
import 'rxjs/add/observable/fromPromise';
import { URLSearchParams } from '@angular/http';

import { Api } from './api.service';
import { SubscriptionOfferService } from './subscription-offer.service';

import {
  Statistic,
  StatisticChurn,
  StatisticSubscription,
  StatisticOption,
  SubscriptionOffer,
  AverageLengthCancelledSubscription,
  AverageLengthSubscription,
  DateCountStatistic,
  SponsoringStatistic,
  MonthlyCohorts,
  User
} from '../entities/index';
import { Log, LocalCache } from '../utils/index';

const fillMisingStats = function(churnStatsData: StatisticChurn|StatisticSubscription|StatisticOption) {
  _.forOwn(churnStatsData, function(value, key) {
      if (key !== 'date' && value.length > churnStatsData['date'].length) {
        value.splice(-1, 1);
      }
      else if (key !== 'date' && value.length < churnStatsData['date'].length) {
          value.push('0');
    }
  });
};


@Injectable()
export class StatisticService {
  constructor(
    protected http: Api,
    protected localCache: LocalCache,
    protected subscriptionOfferService: SubscriptionOfferService
  ) {}

  @Log('green')
  retailerStats(user: User = null): Observable<Statistic[]> {
    return this.getAll('retailer', user);
  }

  @Log('green')
  statusStats(user: User = null): Observable<Statistic[]> {
    return this.getAll('status', user);
  }

  @Log('green')
  activationStats(retailerId: number = null): Observable<Statistic[]> {
    const params = new URLSearchParams();
    if (retailerId) {
      params.set('retailer_id', String(retailerId));
    }

    return this.getAll('activation', null, params);
  }

  @Log('green')
  activationStillActiveStats(retailerId: number = null): Observable<Statistic[]> {
    const params = new URLSearchParams();
    if (retailerId) {
      params.set('retailer_id', String(retailerId));
    }

    return this.getAll('activation_still_active', null, params);
  }

  @Log('green')
  expiredNoSubscriptionStats(retailerId: number = null): Observable<Statistic[]> {
    const params = new URLSearchParams();
    if (retailerId) {
      params.set('retailer_id', String(retailerId));
    }

    return this.getAll('expired_no_subscription', null, params);
  }

  @Log('green')
  expiredWithSubscriptionStats(retailerId: number = null): Observable<Statistic[]> {
    const params = new URLSearchParams();
    if (retailerId) {
      params.set('retailer_id', String(retailerId));
    }

    return this.getAll('expired_with_subscription', null, params);
  }

  @Log('green')
  optionActivationStats(optionState: string = null): Observable<Statistic[]> {
    const params = new URLSearchParams();
    if (optionState) {
      params.set('option_state', optionState);
    }

    return this.getAll('options_activation', null, params);
  }

  @Log('green')
  monthlyCohorts(): Observable<MonthlyCohorts[]> {
    return this.http.get('stats/monthly_pseudo_cohorts');
  }

  @Log('green')
  typeStats(user: User = null): Observable<Statistic[]> {
    return this.getAll('type', user);
  }

  @Log('green')
  subscriptionStats(siteAdmin: string = null): Observable<StatisticSubscription> {
    return this.getAll('all_subscriptions').map(res => {
      const subscriptionStatsData: StatisticSubscription = new StatisticSubscription();
      let lastDate: string = null;

      // Remove Weenect's stats if user is a partner admin
      res = _.filter(res, function(value: Statistic) {
        return !siteAdmin || value.subscription_type.endsWith(siteAdmin);
      });

      _.forEach(res, function(value): void {
          const subscriptionStatsKey = value.type + '_' + value.subscription_type;

          // If date change fill in missing values with 0
          if (lastDate !== value.date) {
            fillMisingStats(subscriptionStatsData);
            lastDate = value.date;
          }

          (<any>subscriptionStatsData)[subscriptionStatsKey] = (<any>subscriptionStatsData)[subscriptionStatsKey] || [];
          (<any>subscriptionStatsData)[subscriptionStatsKey].push(value.value_int);

          if (subscriptionStatsData.date.indexOf(value.date) === -1) {
            subscriptionStatsData.date.push(value.date);
          }
      });
      fillMisingStats(subscriptionStatsData);
      return subscriptionStatsData;
    });
  }

  @Log('green')
  optionStats(siteAdmin: string = null): Observable<StatisticOption> {
    return this.getAll('all_options').map(res => {
      const optionStatsData: StatisticOption = new StatisticOption();
      let lastDate: string = null;

      // Remove Weenect's stats if user is a partner admin
      res = _.filter(res, function(value: Statistic) {
        return !siteAdmin || value.option_type.endsWith(siteAdmin);
      });

      _.forEach(res, function(value): void {
        const optionStatsKey = value.type + '_' + value.option_type;

        // If date change fill in missing values with 0
        if (lastDate !== value.date) {
          fillMisingStats(optionStatsData);
          lastDate = value.date;
        }

        (<any>optionStatsData)[optionStatsKey] = (<any>optionStatsData)[optionStatsKey] || [];
        (<any>optionStatsData)[optionStatsKey].push(value.value_int);

        if (optionStatsData.date.indexOf(value.date) === -1) {
          optionStatsData.date.push(value.date);
        }
      });
      fillMisingStats(optionStatsData);
      return optionStatsData;
    });
  }

  @Log('green')
  churnStats(retailerId: number = null): Observable<StatisticChurn> {
    return this.getAll('all_trackers').map(res => {
      // Format churn statistics
      const churnStatsData: StatisticChurn = new StatisticChurn();
      let lastDate: string = null;

      // Remove Weenect's stats if user is a partner admin
      res = _.filter(res, function(value) {
        return !retailerId || value.retailer_id === retailerId;
      });

      _.forEach(res, function(value): void {
          let churnStatsKey: string = value.type;
          // If date change fill in missing values with 0
          if (lastDate !== value.date) {
            fillMisingStats(churnStatsData);
            lastDate = value.date;
          }

          churnStatsKey += '_' + value.retailer_id;
          if (!!value.tracker_type) {
            churnStatsKey += '_' + value.tracker_type;
          }

          (<any>churnStatsData)[churnStatsKey] = (<any>churnStatsData)[churnStatsKey] || [];
          (<any>churnStatsData)[churnStatsKey].push(value.value_int);

          if (churnStatsData.date.indexOf(value.date) === -1) {
            churnStatsData.date.push(value.date);
          }
      });
      fillMisingStats(churnStatsData);
      return churnStatsData;
    });
  }

  getAll(statType: string, user ?: User, params ?: URLSearchParams): Observable<Statistic[]> {
    params = params || new URLSearchParams();
    if (user && user.role_retailer_id) {
      params.set('retailer_id', String(user.role_retailer_id));
    }
    return this.http.get(`stats/${statType}`, { search: params });
  }

  @Log('green')
  countSubscriptionPerMonth(status: string, period: number, payment: string, on_canceled_at ?: boolean): Observable<DateCountStatistic[]> {
    const params = new URLSearchParams();
    if (status) {
      params.set('status', String(status));
    }
    if (period) {
      params.set('period', String(period));
    }
    if (payment) {
      params.set('payment', String(payment));
    }
    if (on_canceled_at) {
      params.set('count_on_canceled_at', String(1));
    }

    return this.http.get(`stats/subscription`, { search: params });
  }

  @Log('green')
  nbAverageTrackerPerUser(): Observable<number> {
      // 7 days cache
      return this.localCache.cachedObservable(
        'nb_average_tracker_per_user',
        this.http.get('stats/average_tracker_per_account').map(result => result['result']),
        604800
      ).map(result => Number(result));
  }

  mapOffers(offers: SubscriptionOffer[]) {
    return function(item: AverageLengthSubscription) {
      item.offer = offers.find((offer: SubscriptionOffer) => offer.id === item.offer_id);
      return item;
    };
  }

  @Log('green')
  averageLengthCancelledSubscription(): Observable<AverageLengthCancelledSubscription> {
    // 7 days cache
    return this.localCache.cachedObservable(
      'average_length_cancelled_subscription',
      this.http.get('stats/average_canceled_subscription'),
      604800
    )
    .zip(Observable.fromPromise(this.subscriptionOfferService.getAll()))
    .map((result: [AverageLengthCancelledSubscription, SubscriptionOffer[]]) => {
      result[0].all.map(this.mapOffers(result[1]));
      result[0].auto.map(this.mapOffers(result[1]));
      result[0].manual.map(this.mapOffers(result[1]));
      return result[0];
    });
  }

  @Log('green')
  averageLengthActiveSubscription(): Observable<AverageLengthSubscription[]> {
    // 7 days cache
    return this.localCache.cachedObservable(
      'average_length_active_subscription',
      this.http.get('stats/average_active_subscription'),
      604800
    )
    .zip(Observable.fromPromise(this.subscriptionOfferService.getAll()))
    .map((result: [AverageLengthSubscription[], SubscriptionOffer[]]) => {
      result[0].map(this.mapOffers(result[1]));
      return result[0];
    });
  }

  @Log('green')
  sponsoring(): Observable<SponsoringStatistic> {
    // 7 days cache
    return this.localCache.cachedObservable(
      'sponsoring',
      this.http.get('stats/sponsoring'),
      604800
    );
  }
}



// WEBPACK FOOTER //
// /app/src/app/services/statistic.service.ts